const issuer =
  import.meta.env.VITE_MYEP_OIDC_ISSUER ??
  'https://auth.my.eperformances.com/realms/my-eperformances';
const clientId = import.meta.env.VITE_MYEP_OIDC_CLIENT_ID ?? 'my-eperformances-web';
const tokenStorageKey = 'myep.tokens';
const verifierStorageKey = 'myep.pkce.verifier';
const stateStorageKey = 'myep.pkce.state';

export interface TokenSet {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
}

export function hasToken(): boolean {
  return readTokens() !== null;
}

export async function login(): Promise<void> {
  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = await pkceChallenge(verifier);
  sessionStorage.setItem(verifierStorageKey, verifier);
  sessionStorage.setItem(stateStorageKey, state);

  const url = new URL(`${issuer}/protocol/openid-connect/auth`);
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', callbackUrl());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export async function completeLogin(search: string): Promise<void> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(stateStorageKey);
  const verifier = sessionStorage.getItem(verifierStorageKey);

  if (!code || !state || state !== expectedState || !verifier) {
    throw new Error('Login konnte nicht abgeschlossen werden.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: callbackUrl(),
    code_verifier: verifier,
  });
  const response = await fetch(`${issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) throw new Error(`Token-Austausch fehlgeschlagen: ${response.status}`);
  storeTokenResponse(await response.json());
  sessionStorage.removeItem(verifierStorageKey);
  sessionStorage.removeItem(stateStorageKey);
}

export async function getAccessToken(): Promise<string | null> {
  const tokens = readTokens();
  if (!tokens) return null;
  if (tokens.expiresAt > Date.now() + 30_000) return tokens.accessToken;
  if (!tokens.refreshToken) return null;
  return refresh(tokens.refreshToken);
}

export function logout(): void {
  const tokens = readTokens();
  localStorage.removeItem(tokenStorageKey);
  const url = new URL(`${issuer}/protocol/openid-connect/logout`);
  url.searchParams.set('post_logout_redirect_uri', window.location.origin);
  if (tokens?.idToken) url.searchParams.set('id_token_hint', tokens.idToken);
  window.location.assign(url.toString());
}

function readTokens(): TokenSet | null {
  const raw = localStorage.getItem(tokenStorageKey);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    localStorage.removeItem(tokenStorageKey);
    return null;
  }
}

async function refresh(refreshToken: string): Promise<string | null> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });
  const response = await fetch(`${issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    localStorage.removeItem(tokenStorageKey);
    return null;
  }
  const tokens = storeTokenResponse(await response.json());
  return tokens.accessToken;
}

function storeTokenResponse(value: unknown): TokenSet {
  const payload = value as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
  };
  const tokens: TokenSet = {
    accessToken: payload.access_token,
    expiresAt: Date.now() + (payload.expires_in ?? 300) * 1000,
  };
  if (payload.refresh_token) tokens.refreshToken = payload.refresh_token;
  if (payload.id_token) tokens.idToken = payload.id_token;
  localStorage.setItem(tokenStorageKey, JSON.stringify(tokens));
  return tokens;
}

function callbackUrl(): string {
  return `${window.location.origin}/callback`;
}

async function pkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64Url(new Uint8Array(digest));
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64Url(bytes).slice(0, length);
}

function base64Url(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
